import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTickerSearch } from "../hooks/useQueries";
import { CombavaSpinner } from "./CombavaSpinner";

interface SearchBarProps {
  size?: "default" | "hero" | "mobile";
}

export default function SearchBar({ size = "default" }: SearchBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce the search query
  const [debouncedQuery, setDebouncedQuery] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 200);
    return () => clearTimeout(t);
  }, [query]);

  const { data: searchData, isFetching } = useTickerSearch(debouncedQuery);
  const matches = searchData?.tickers ?? [];

  const handleSelect = useCallback(
    (ticker: string) => {
      setQuery("");
      setOpen(false);
      navigate(`/stocks/${ticker.toLowerCase()}/overview`);
    },
    [navigate],
  );

  // Click outside → close
  useEffect(() => {
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node))
        setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Cmd+K global shortcut
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const isHero = size === "hero";

  return (
    <div
      ref={wrapperRef}
      className={`relative ${
        isHero
          ? "w-full max-w-2xl"
          : "flex flex-col flex-1 w-full min-w-0"
      }`}
    >
      <div
        className={`flex w-full items-center rounded-lg bg-card-dark border border-card-border focus-within:border-primary transition-colors gap-2 ${
          isHero ? "h-14 px-5 rounded-xl" : "h-10 px-3"
        }`}
      >
        <span
          className={`material-symbols-outlined text-text-secondary shrink-0 ${
            isHero ? "text-[24px]" : "text-[20px]"
          }`}
        >
          search
        </span>
        <input
          ref={inputRef}
          className={`w-full bg-transparent border-0 ring-0 text-white placeholder:text-text-secondary focus:outline-none focus:ring-0 focus:border-0 ${
            isHero ? "text-lg" : "text-sm"
          }`}
          placeholder="Search any ticker or company..."
          autoComplete="off"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => query.trim() && setOpen(true)}
        />
        {isFetching && (
          <CombavaSpinner className="size-4 shrink-0" />
        )}
        {!isHero && !isFetching && (
          <kbd className="hidden sm:inline text-[10px] text-text-secondary border border-card-border rounded px-1.5 py-0.5 font-mono">
            ⌘K
          </kbd>
        )}
      </div>

      {/* Dropdown */}
      {open && debouncedQuery.length > 0 && (
        <div
          className={`absolute left-0 right-0 bg-card-dark border border-card-border rounded-lg shadow-2xl z-[60] max-h-80 overflow-y-auto custom-scrollbar ${
            isHero ? "top-16" : "top-11"
          }`}
        >
          {matches.length === 0 && !isFetching && (
            <div className="px-4 py-6 text-center text-text-secondary text-sm">
              No results for "{debouncedQuery}"
            </div>
          )}
          {matches.map((t) => (
            <div
              key={t.cik}
              className="px-4 py-3 hover:bg-card-border/50 cursor-pointer flex items-center gap-3 transition-colors"
              onClick={() => handleSelect(t.ticker)}
            >
              <span className="font-mono font-bold text-primary text-sm min-w-[4.5rem]">
                {t.ticker}
              </span>
              <span className="text-sm text-text-secondary truncate flex-1">
                {t.name}
              </span>
              {t.exchange && (
                <span className="text-[10px] text-text-secondary/60 font-mono uppercase">
                  {t.exchange}
                </span>
              )}
              {t.ingestion_completed ? (
                <span className="flex items-center gap-1 text-[10px] font-medium text-accent-green px-1.5 py-0.5 rounded bg-accent-green/10 border border-accent-green/20 shrink-0">
                  <span className="size-1.5 rounded-full bg-accent-green" />
                  Data
                </span>
              ) : (
                <span className="text-[10px] font-medium text-text-secondary/50 px-1.5 py-0.5 rounded bg-card-border/30 border border-card-border shrink-0">
                  No data
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
